import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import CookieBanner from "@/components/CookieBanner";
import ProtectedAdmin from "@/components/ProtectedAdmin";
import Analytics from "@/components/Analytics";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirm from "@/pages/OrderConfirm";
import OrderSuccess from "@/pages/OrderSuccess";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import ShippingReturns from "@/pages/ShippingReturns";
import Authenticity from "@/pages/Authenticity";
import Admin from "@/pages/Admin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminEnquiries from "@/pages/AdminEnquiries";
import AdminOrders from "@/pages/AdminOrders";
import AdminRecovery from "@/pages/AdminRecovery";
import AdminTestimonials from "@/pages/AdminTestimonials";
import AdminLogin from "@/pages/AdminLogin";
import OrderTrack from "@/pages/OrderTrack";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      // small delay to let target render
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
      return;
    }
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash]);
  return null;
}

function NotFound() {
  return (
    <div className="pt-40 pb-32 text-center" data-testid="not-found-page">
      <p className="eyebrow">Lost</p>
      <h1 className="font-display text-6xl md:text-8xl mt-4 tracking-tighter">Off the path.</h1>
      <a href="/" className="btn-primary mt-8 inline-flex">Back to the shop</a>
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <ScrollToTop />
            <Analytics />
            <Header />
            <CartDrawer />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order/success" element={<OrderSuccess />} />
                <Route path="/order/track" element={<OrderTrack />} />
                <Route path="/order/:id" element={<OrderConfirm />} />
                <Route path="/about" element={<About />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/shipping-returns" element={<ShippingReturns />} />
                <Route path="/authenticity" element={<Authenticity />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedAdmin>
                      <AdminDashboard />
                    </ProtectedAdmin>
                  }
                />
                <Route
                  path="/admin/products"
                  element={
                    <ProtectedAdmin>
                      <Admin />
                    </ProtectedAdmin>
                  }
                />
                <Route
                  path="/admin/orders"
                  element={
                    <ProtectedAdmin>
                      <AdminOrders />
                    </ProtectedAdmin>
                  }
                />
                <Route
                  path="/admin/recovery"
                  element={
                    <ProtectedAdmin>
                      <AdminRecovery />
                    </ProtectedAdmin>
                  }
                />
                <Route
                  path="/admin/testimonials"
                  element={
                    <ProtectedAdmin>
                      <AdminTestimonials />
                    </ProtectedAdmin>
                  }
                />
                <Route
                  path="/admin/enquiries"
                  element={
                    <ProtectedAdmin>
                      <AdminEnquiries />
                    </ProtectedAdmin>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
            <CookieBanner />
            <Toaster
              position="top-center"
              theme="light"
              toastOptions={{
                style: {
                  borderRadius: 0,
                  background: "#121212",
                  color: "#f9f9f6",
                  border: "1px solid #121212",
                  fontFamily: "Outfit, sans-serif",
                  letterSpacing: "0.04em",
                },
              }}
            />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
